import React, { useState } from 'react';
import { api, CacheFlushResult } from '../api';
import { Callout, Input, Section, Subsection } from './primitives';
import { OperationFailure, OperationResult } from './operation/OperationResult';
import { Field } from './ui';

interface CacheControlCardProps {
  environmentId: string;
}

/** What was asked for, in the card's words, so the answer can name it. */
const flushWords: Record<string, { name: string; done: string }> = {
  all: { name: 'Flushing all caches', done: 'All Magento caches flushed' },
  full_page: { name: 'Flushing the full page cache', done: 'Full page cache flushed' },
  config: { name: 'Flushing the configuration cache', done: 'Configuration cache flushed' },
};

interface Ran {
  /** What the person asked for, as a noun for a failure: "Flushing the full page cache". */
  name: string;
  /** What a success says. */
  done: string;
  at: string;
}

type Outcome =
  | { kind: 'answer'; ran: Ran; result: CacheFlushResult; retry: () => void }
  | { kind: 'error'; ran: Ran; message: string; retry: () => void };

export const CacheControlCard: React.FC<CacheControlCardProps> = ({ environmentId }) => {
  const [targetUrl, setTargetUrl] = useState('');
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [inputNotice, setInputNotice] = useState<string | null>(null);

  const perform = async (action: string, words: { name: string; done: string }, call: () => Promise<CacheFlushResult>, retry: () => void) => {
    setLoadingAction(action);
    setInputNotice(null);
    setOutcome(null);
    try {
      const result = await call();
      setOutcome({ kind: 'answer', ran: { ...words, at: new Date().toISOString() }, result, retry });
    } catch (err) {
      setOutcome({
        kind: 'error',
        ran: { ...words, at: new Date().toISOString() },
        message: err instanceof Error && err.message ? err.message : 'The server gave no reason.',
        retry,
      });
    } finally {
      setLoadingAction(null);
    }
  };

  const handleFlush = (cacheType: string) => {
    const words = flushWords[cacheType] ?? { name: 'Flushing caches', done: 'Caches flushed' };
    return perform('flush:' + cacheType, words, () => api.flushCache(environmentId, cacheType), () => void handleFlush(cacheType));
  };

  const purge = (target: string) =>
    perform('purge', { name: `Purging ${target}`, done: `Purge sent for ${target}` }, () => api.purgeCdn(environmentId, target), () => void purge(target));

  const handlePurge = (event: React.FormEvent) => {
    event.preventDefault();
    const target = targetUrl.trim();
    if (!target) {
      setInputNotice('Enter a URL or a path to purge.');
      return;
    }
    return purge(target);
  };

  return (
    <Section
      title="Caches"
      testId="cache-control"
    >
      {inputNotice ? (
        <Callout tone="warning" role="status" title={inputNotice} />
      ) : null}

      {outcome?.kind === 'error' ? (
        <OperationFailure
          verdict={`${outcome.ran.name} did not complete`}
          at={outcome.ran.at}
          scope="The request did not get an answer, so it is not known whether anything was changed."
          reason={outcome.message}
          onRetry={outcome.retry}
          testId="cache-result"
        />
      ) : null}

      {outcome?.kind === 'answer' && !outcome.result.succeeded ? (
        <OperationFailure
          verdict={`${outcome.ran.name} did not complete`}
          at={outcome.ran.at}
          scope="The server reported a failure."
          reason={outcome.result.error_output || 'The server gave no reason.'}
          output={outcome.result.output}
          onRetry={outcome.retry}
          testId="cache-result"
        />
      ) : null}

      {outcome?.kind === 'answer' && outcome.result.succeeded ? (
        <OperationResult
          tone="success"
          atLabel="Ran"
          verdict={outcome.ran.done}
          at={outcome.ran.at}
          scope={
            <>
              Finished in <span className="tabular-nums">{outcome.result.duration_ms} ms</span>
              {outcome.result.flushed_tags && outcome.result.flushed_tags.length > 0 ? ` · ${outcome.result.flushed_tags.length} cache tags flushed` : ''}
              {outcome.result.output?.trim() ? '' : ' · no output'}
            </>
          }
          output={outcome.result.output}
          testId="cache-result"
        />
      ) : null}

      <Subsection title="Magento caches">
        <div className="ssh-actions">
          <button type="button" onClick={() => handleFlush('all')} disabled={loadingAction !== null} className="button">
            {loadingAction === 'flush:all' ? 'Flushing all caches' : 'Flush all caches'}
          </button>
          <button
            type="button"
            onClick={() => handleFlush('full_page')}
            disabled={loadingAction !== null}
            className="button button--secondary"
          >
            {loadingAction === 'flush:full_page' ? 'Flushing the full page cache' : 'Flush full page cache'}
          </button>
          <button
            type="button"
            onClick={() => handleFlush('config')}
            disabled={loadingAction !== null}
            className="button button--secondary"
          >
            {loadingAction === 'flush:config' ? 'Flushing the configuration cache' : 'Flush configuration cache'}
          </button>
        </div>
      </Subsection>

      <Subsection title="Purge one URL" description="Sent to Varnish on the server, not a CDN.">
        <form className="ssh-inline-field" onSubmit={handlePurge}>
          <Field label="URL or path">
            <Input
              id="cdn-purge-url"
              type="text"
              placeholder="https://store.example.com/shoes or /catalog/category/view/id/12"
              value={targetUrl}
              onChange={(e) => setTargetUrl(e.target.value)}
            />
          </Field>
          <button type="submit" disabled={loadingAction !== null} className="button button--secondary">
            {loadingAction === 'purge' ? 'Purging' : 'Purge URL'}
          </button>
        </form>
      </Subsection>
    </Section>
  );
};
